import { ApolloClient } from '@apollo/client';
import * as React from 'react';

import {
  IEventSearch,
  IFileSearch,
  IGroupSearch,
  IMemberSearch,
  IOrganizationSearch,
  IPostSearch,
  ITagSearch,
} from '@/client/legacy-shared-modules/search/search-types';
import { MemberIndexViewableField } from '@/components/GlobalSearch/types';

import { SearchAIResults_EventsByIds } from '../../graphql-types/SearchAIResults_EventsByIds';
import { SearchAIResults_GroupsByIds } from '../../graphql-types/SearchAIResults_GroupsByIds';
import { SearchAIResults_OrganizationsByIds } from '../../graphql-types/SearchAIResults_OrganizationsByIds';
import { SearchAIResults_UsersByIds } from '../../graphql-types/SearchAIResults_UsersByIds';
import { SearchAIResults_VersionFilesByIds } from '../../graphql-types/SearchAIResults_VersionFilesByIds';
import { SearchAIResults_postsByIdsForAiAsk } from '../../graphql-types/SearchAIResults_postsByIdsForAiAsk';
import {
  EVENTS_BY_IDS_FOR_AI_SEARCH,
  GROUPS_BY_IDS_FOR_AI_SEARCH,
  ORGANIZATIONS_BY_IDS_FOR_AI_SEARCH,
  POSTS_BY_IDS_FOR_AI_SEARCH,
  TAGS_BY_IDS_FOR_AI_SEARCH,
  USERS_BY_IDS_FOR_AI_SEARCH,
  VERSIONFILES_BY_IDS_FOR_AI_SEARCH,
} from '../../searchResultQueries';
import {
  AiSearchResultsSectionState,
  ImplementedAiSearchIndexKey,
} from '../../types';
import { AiAskIndexData } from '../../utils/aiAskHelpers';
import { fetchGraphqlRecordsByIds } from '../../utils/fetchRecordsByIds';
import { AiAskSearchIndexBlock } from '../../utils/normalizeIndexBlocks';
import { sortAiAskSearchIndexBlocks } from '../../utils/sectionOrder';
import { EventsList } from '../events/EventsList';
import { mapEventsByIdsToEventHits } from '../events/mapToHits';
import { FilesList } from '../files/FilesList';
import { mapVersionFilesByIdsToFileHits } from '../files/mapToHits';
import { GroupsList } from '../groups/GroupsList';
import { mapGroupsByIdsToGroupHits } from '../groups/mapToHits';
import { TagsList } from '../tags/TagsList';
import { mapTagsByIdsToTagHits } from '../tags/mapToHits';
import { MembersList } from '../members/MembersList';
import { mapUsersByIdsToMemberHits } from '../members/mapToHits';
import { OrganizationsList } from '../organizations/OrganizationsList';
import { mapOrganizationsByIdsToOrganizationHits } from '../organizations/mapToHits';
import { PostsList } from '../posts/PostsList';
import { mapPostsByIdsToPostHits } from '../posts/mapToHits';
import { SearchAIResults_TagsByIds } from '../../graphql-types/SearchAIResults_TagsByIds';

type IndexResultsPayload = {
  count: number | null;
  object_ids: string[];
};

type IndexSectionHandler = {
  fetchSection: (
    apolloClient: ApolloClient<object>,
    indexPayload: IndexResultsPayload,
    memberViewableField: MemberIndexViewableField
  ) => Promise<AiSearchResultsSectionState>;
  renderList: (
    sectionState: AiSearchResultsSectionState,
    indexData: AiAskIndexData
  ) => React.ReactNode;
};

const INDEX_SECTION_HANDLERS: Record<
  ImplementedAiSearchIndexKey,
  IndexSectionHandler
> = {
  members: {
    async fetchSection(apolloClient, indexPayload, memberViewableField) {
      const users = await fetchGraphqlRecordsByIds<
        SearchAIResults_UsersByIds,
        SearchAIResults_UsersByIds['usersByIdsForAiAsk']['results'][number]
      >(apolloClient, {
        query: USERS_BY_IDS_FOR_AI_SEARCH,
        variables: { input: { userIds: indexPayload.object_ids } },
        selectResults: (data) => data?.usersByIdsForAiAsk?.results,
      });

      return {
        index: 'members',
        count: indexPayload.count,
        hits: mapUsersByIdsToMemberHits(
          users,
          indexPayload.object_ids,
          memberViewableField
        ),
      };
    },
    renderList(sectionState, indexData) {
      if (sectionState.index !== 'members') return null;
      return (
        <MembersList
          hits={sectionState.hits as IMemberSearch[]}
          viewableAttributesField={
            indexData.memberIndexViewableField as MemberIndexViewableField
          }
          searchableAttributesField={indexData.memberIndexSearchableField}
        />
      );
    },
  },
  posts: {
    async fetchSection(apolloClient, indexPayload, memberViewableField) {
      const posts = await fetchGraphqlRecordsByIds<
        SearchAIResults_postsByIdsForAiAsk,
        SearchAIResults_postsByIdsForAiAsk['postsByIdsForAiAsk']['results'][number]
      >(apolloClient, {
        query: POSTS_BY_IDS_FOR_AI_SEARCH,
        variables: { input: { postIds: indexPayload.object_ids } },
        selectResults: (data) => data?.postsByIdsForAiAsk?.results,
      });

      return {
        index: 'posts',
        count: indexPayload.count,
        hits: mapPostsByIdsToPostHits(
          posts,
          indexPayload.object_ids,
          memberViewableField
        ),
      };
    },
    renderList(sectionState) {
      if (sectionState.index !== 'posts') return null;
      return <PostsList hits={sectionState.hits as IPostSearch[]} />;
    },
  },
  events: {
    async fetchSection(apolloClient, indexPayload) {
      const events = await fetchGraphqlRecordsByIds<
        SearchAIResults_EventsByIds,
        SearchAIResults_EventsByIds['eventsByIds']['results'][number]
      >(apolloClient, {
        query: EVENTS_BY_IDS_FOR_AI_SEARCH,
        variables: { ids: indexPayload.object_ids },
        selectResults: (data) => data?.eventsByIds?.results,
      });

      return {
        index: 'events',
        count: indexPayload.count,
        hits: mapEventsByIdsToEventHits(events, indexPayload.object_ids),
      };
    },
    renderList(sectionState) {
      if (sectionState.index !== 'events') return null;
      return <EventsList hits={sectionState.hits as IEventSearch[]} />;
    },
  },
  organizations: {
    async fetchSection(apolloClient, indexPayload) {
      const organizations = await fetchGraphqlRecordsByIds<
        SearchAIResults_OrganizationsByIds,
        SearchAIResults_OrganizationsByIds['sponsorsByIds']['results'][number]
      >(apolloClient, {
        query: ORGANIZATIONS_BY_IDS_FOR_AI_SEARCH,
        variables: { organizationIds: indexPayload.object_ids },
        selectResults: (data) => data?.sponsorsByIds?.results,
      });

      return {
        index: 'organizations',
        count: indexPayload.count,
        hits: mapOrganizationsByIdsToOrganizationHits(
          organizations,
          indexPayload.object_ids
        ),
      };
    },
    renderList(sectionState) {
      if (sectionState.index !== 'organizations') return null;
      return (
        <OrganizationsList hits={sectionState.hits as IOrganizationSearch[]} />
      );
    },
  },
  groups: {
    async fetchSection(apolloClient, indexPayload) {
      const groups = await fetchGraphqlRecordsByIds<
        SearchAIResults_GroupsByIds,
        SearchAIResults_GroupsByIds['groupsByIds']['results'][number]
      >(apolloClient, {
        query: GROUPS_BY_IDS_FOR_AI_SEARCH,
        variables: { input: { groupIds: indexPayload.object_ids } },
        selectResults: (data) => data?.groupsByIds?.results,
      });

      return {
        index: 'groups',
        count: indexPayload.count,
        hits: mapGroupsByIdsToGroupHits(groups, indexPayload.object_ids),
      };
    },
    renderList(sectionState) {
      if (sectionState.index !== 'groups') return null;
      return <GroupsList hits={sectionState.hits as IGroupSearch[]} />;
    },
  },
  tags: {
    async fetchSection(apolloClient, indexPayload) {
      const tags = await fetchGraphqlRecordsByIds<
        SearchAIResults_TagsByIds,
        SearchAIResults_TagsByIds['tagsByIds']['results'][number]
      >(apolloClient, {
        query: TAGS_BY_IDS_FOR_AI_SEARCH,
        variables: { input: { tagIds: indexPayload.object_ids } },
        selectResults: (data) => data?.tagsByIds?.results,
      });

      return {
        index: 'tags',
        count: indexPayload.count,
        hits: mapTagsByIdsToTagHits(tags, indexPayload.object_ids),
      };
    },
    renderList(sectionState) {
      if (sectionState.index !== 'tags') return null;
      return <TagsList hits={sectionState.hits as ITagSearch[]} />;
    },
  },
  files: {
    async fetchSection(apolloClient, indexPayload) {
      const versionFiles = await fetchGraphqlRecordsByIds<
        SearchAIResults_VersionFilesByIds,
        SearchAIResults_VersionFilesByIds['versionFilesByIdsForAiAsk']['results'][number]
      >(apolloClient, {
        query: VERSIONFILES_BY_IDS_FOR_AI_SEARCH,
        variables: { input: { versionFileIds: indexPayload.object_ids } },
        selectResults: (data) => data?.versionFilesByIdsForAiAsk?.results,
      });

      return {
        index: 'files',
        count: indexPayload.count,
        hits: mapVersionFilesByIdsToFileHits(
          versionFiles,
          indexPayload.object_ids
        ),
      };
    },
    renderList(sectionState) {
      if (sectionState.index !== 'files') return null;
      return <FilesList hits={sectionState.hits as IFileSearch[]} />;
    },
  },
};

export function isSupportedAiSearchIndex(
  indexName: string
): indexName is ImplementedAiSearchIndexKey {
  return indexName in INDEX_SECTION_HANDLERS;
}

export async function fetchAiSearchSectionState(
  apolloClient: ApolloClient<object>,
  indexName: string,
  indexPayload: IndexResultsPayload,
  memberViewableField: MemberIndexViewableField
): Promise<AiSearchResultsSectionState> {
  const normalizedIndexName = indexName.trim().toLowerCase();
  if (!isSupportedAiSearchIndex(normalizedIndexName)) {
    throw new Error(`View Results for "${indexName}" is not supported yet.`);
  }

  return INDEX_SECTION_HANDLERS[normalizedIndexName].fetchSection(
    apolloClient,
    indexPayload,
    memberViewableField
  );
}

export async function fetchAiSearchPageSections(
  apolloClient: ApolloClient<object>,
  indexBlocks: AiAskSearchIndexBlock[],
  memberViewableField: MemberIndexViewableField
): Promise<{
  sections: AiSearchResultsSectionState[];
  skippedIndexes: string[];
}> {
  const sortedIndexBlocks = sortAiAskSearchIndexBlocks(indexBlocks);
  const skippedIndexes: string[] = [];
  const sections: AiSearchResultsSectionState[] = [];

  const settledSectionResults = await Promise.allSettled(
    sortedIndexBlocks.map(async (indexBlock) => {
      const normalizedIndexName = indexBlock.index.trim().toLowerCase();

      if (!isSupportedAiSearchIndex(normalizedIndexName)) {
        skippedIndexes.push(normalizedIndexName);
        return null;
      }

      if (indexBlock.object_ids.length === 0) {
        return null;
      }

      return fetchAiSearchSectionState(
        apolloClient,
        normalizedIndexName,
        {
          count: indexBlock.count,
          object_ids: indexBlock.object_ids,
        },
        memberViewableField
      );
    })
  );

  for (const settledResult of settledSectionResults) {
    if (settledResult.status === 'fulfilled' && settledResult.value) {
      sections.push(settledResult.value);
      continue;
    }

    if (settledResult.status === 'rejected') {
      const errorMessage =
        settledResult.reason instanceof Error
          ? settledResult.reason.message
          : String(settledResult.reason);
      throw new Error(errorMessage);
    }
  }

  return { sections, skippedIndexes };
}

export function renderAiSearchResultsList(
  sectionState: AiSearchResultsSectionState,
  indexData: AiAskIndexData
): React.ReactNode {
  if (!isSupportedAiSearchIndex(sectionState.index)) {
    return null;
  }
  return INDEX_SECTION_HANDLERS[sectionState.index].renderList(
    sectionState,
    indexData
  );
}
